import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import { generatePolicyCardInfo } from "../../../../utils/policyCardUtils";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const PersonalCyberReviewCancellationPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { handleNextSubstep } = usePolicyCancellationFlow();
  const { i18n, t } = useTranslation();

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get policy detail from Redux (same selector as travel uses)
  const policyDetailsInfo = useSelector(
    (state) => state.policyDetails?.PolicyDetails?.PolicyHeader
  );
  // Get logged in user profile info
  const profileInfo = useSelector(selectProfileData);

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== PERSONAL CYBER POLICY CANCEL DATA DEBUG ===");
  console.log("Full policyDetailsInfo no json:", policyDetailsInfo);
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2)
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("PlanName:", policyDetailsInfo?.PlanName);
  console.log("ProductCode:", policyDetailsInfo?.ProductCode);
  console.log("Premium:", policyDetailsInfo?.Premium);
  console.log("=== END DEBUG ===");

  // Helper function for RTL-aware currency formatting - always use English numerals
  const formatCurrency = (amount) => {
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse rtl:justify-end">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  // Sum Insured is returned by the API in USD (e.g. 500) — display it with the
  // USD label and thousand separators, e.g. "USD 500" / "500 دولار أمريكي".
  const formatSumInsuredUSD = (amount) => {
    if (amount === undefined || amount === null || amount === "") {
      return translate("N/A", "غير متوفر");
    }
    const formatted = Number(amount).toLocaleString("en-US");
    return translate(`USD ${formatted}`, `${formatted} دولار أمريكي`);
  };

  // Helper function to format date to English format (10 July 2025)
  const formatDateToEnglish = (dateStr) => {
    if (!dateStr) return "--";

    try {
      const parts = dateStr.split("/");
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Generate policy card info using utility function
  console.log("=== POLICY CARD DATA DEBUG ===");
  console.log("policyDetailsInfo for card:", policyDetailsInfo);
  console.log("CPR:", policyDetailsInfo?.CPR);
  console.log("InsuredCPR:", policyDetailsInfo?.InsuredCPR);
  console.log("Party:", policyDetailsInfo?.Party);
  console.log("FirstName:", policyDetailsInfo?.FirstName);
  console.log("LastName:", policyDetailsInfo?.LastName);
  console.log("Party.FirstName:", policyDetailsInfo?.Party?.FirstName);
  console.log("Party.LastName:", policyDetailsInfo?.Party?.LastName);

  const policyCardInfo = generatePolicyCardInfo(
    policyDetailsInfo,
    getClientSource(),
    profileInfo // Pass logged in user profile data
  );

  console.log("Generated policyCardInfo:", policyCardInfo);
  console.log("policyCardInfo.Model:", policyCardInfo?.Model);
  console.log("=== END CARD DEBUG ===");

  /* Next */
  const continueClickHandler = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={true}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHandler}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHandler}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg pb-20 max767:p-5 max767:mt-0 max767:pt-0 max767:pb-[50px]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your policy details carefully before proceeding with cancellation.",
                "قم بمراجعة تفاصيل سياستك وقم بتوفير المعلومات اللازمة للمضي قدمًا في عملية الاستسلام."
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول"
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <PolicyCard
                className="mb-4"
                data={policyCardInfo}
                disableCardClick
              />

              {/* Personal Cyber specific table - only 3 fields */}
              <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                <tbody>
                  {/* 1. Full Name - from logged in user */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px] rtl:max767:w-1/2">
                      {translate("Full Name", "الاسم الكامل")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6 rtl:max767:w-1/2">
                      {profileInfo?.FirstName && profileInfo?.LastName
                        ? `${profileInfo.FirstName} ${profileInfo.LastName}`
                        : translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 2. CPR - from logged in user */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px] rtl:max767:w-1/2">
                      {translate("CPR", "الرقم الشخصي")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6 rtl:max767:w-1/2">
                      {profileInfo?.CPR || translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 3. Sum Insured Limit - with proper formatting */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px] rtl:max767:w-1/2">
                      {translate("Sum Insured Limit", "مبلغ التأمين")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6 rtl:max767:w-1/2 rtl:max767:text-left">
                      {formatSumInsuredUSD(policyDetailsInfo?.SumInsured)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default PersonalCyberReviewCancellationPage;
